import styled, { css } from 'styled-components'

// ============================================================
// Design Tokens from AIM Design System
// ============================================================

export const opsColors = {
  brand500: '#FF9900',
  brand700: '#E08800',
  brand800: '#C47700',
  bgPrimary: '#1E1C1A',
  surface: '#2A2723',
  surfaceHover: '#332F2A',
  divider: '#3D3A35',
  dividerFaded: '#33302C',
  bgInput: '#252320',
  textPrimary: '#FFF3E0',
  textSecondary: '#D4C8B8',
  textFaded: '#8A8078',
  textBrand: '#FF9900',
  textOnBrand: '#1E1C1A',
  successBase: '#22C55E',
  warningBase: '#F97316',
  dangerBase: '#EF4444',
  infoBase: '#0EA5E9',
}

// ============================================================
// Layout Wrappers
// ============================================================

export const OpsPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1rem;
  font-family: 'DM Sans', sans-serif;
`

export const OpsGrid = styled.div<{ columns?: string; gap?: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.columns || '1fr'};
  gap: ${(p) => p.gap || '1.25rem'};
`

export const OpsCard = styled.div<{
  noPadding?: boolean
  hoverBorder?: boolean
  borderColor?: string
}>`
  background: ${opsColors.surface};
  border: 1px solid ${(p) => p.borderColor || opsColors.divider};
  border-radius: 10px;
  padding: ${(p) => (p.noPadding ? '0' : '1.25rem')};
  transition: all 0.2s ease;

  ${(p) =>
    p.hoverBorder &&
    css`
      cursor: pointer;
      &:hover {
        border-color: ${opsColors.brand500};
        background: ${opsColors.surfaceHover};
      }
    `}
`

export const OpsCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;

  h3 {
    font-size: 18px;
    font-weight: 800;
    color: ${opsColors.textPrimary};
    margin: 0;
  }
`

// ============================================================
// KPI Strip
// ============================================================

export const KPIStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
`

export const KPICardStyled = styled.div`
  background: ${opsColors.surface};
  border: 1px solid ${opsColors.divider};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`

export const KPIIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(p) => p.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    fill: ${(p) => p.color};
  }
`

export const KPIContent = styled.div`
  display: flex;
  flex-direction: column;

  .kpi-value {
    font-size: 24px;
    font-weight: 800;
    color: ${opsColors.textPrimary};
    line-height: 1.2;
  }

  .kpi-label {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    color: ${opsColors.textFaded};
    letter-spacing: 0.5px;
  }
`

// ============================================================
// Progress Bar
// ============================================================

export const ProgressBarWrapper = styled.div<{ height?: string }>`
  width: 100%;
  height: ${(p) => p.height || '4px'};
  background: ${opsColors.divider};
  border-radius: 4px;
  overflow: hidden;
`

export const ProgressBarFill = styled.div<{ percent: number; color?: string }>`
  height: 100%;
  width: ${(p) => Math.min(p.percent, 100)}%;
  background: ${(p) => p.color || opsColors.brand500};
  border-radius: 4px;
  transition: width 0.5s ease;
`

// ============================================================
// Badges
// ============================================================

export const Badge = styled.span<{ bg: string; color?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.bg}20;
  color: ${(p) => p.color || p.bg};
  white-space: nowrap;
`

export const StatusDot = styled.span<{ color: string; glow?: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
  ${(p) =>
    p.glow &&
    css`
      box-shadow: 0 0 6px ${p.color}80;
    `}
`

// ============================================================
// Alert Card
// ============================================================

export const AlertCardStyled = styled.div<{ severity: string }>`
  background: ${opsColors.surface};
  border: 1px solid ${opsColors.divider};
  border-left: 3px solid
    ${(p) =>
      p.severity === 'critical'
        ? opsColors.dangerBase
        : p.severity === 'warning'
        ? opsColors.warningBase
        : opsColors.infoBase};
  border-radius: 0 8px 8px 0;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

export const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const AlertTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${opsColors.textPrimary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const AlertTimestamp = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${opsColors.textFaded};
  white-space: nowrap;
`

export const AlertDesc = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${opsColors.textFaded};
`

export const AlertAction = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${opsColors.brand500};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

// ============================================================
// Plan Card
// ============================================================

export const PlanCardStyled = styled.div<{ atRisk?: boolean }>`
  background: ${opsColors.surface};
  border: 1px solid ${(p) => (p.atRisk ? `${opsColors.warningBase}66` : opsColors.divider)};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &:hover {
    border-color: ${opsColors.brand500};
    background: ${opsColors.surfaceHover};
  }
`

export const PlanCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

export const PlanName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${opsColors.textPrimary};
`

export const PlanMeta = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${opsColors.textFaded};
`

export const PlanStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
`

export const PlanStat = styled.div`
  display: flex;
  flex-direction: column;

  .stat-value {
    font-size: 14px;
    font-weight: 700;
    color: ${opsColors.textPrimary};
  }

  .stat-label {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    color: ${opsColors.textFaded};
  }
`

// ============================================================
// Tab Bar
// ============================================================

export const TabBarWrapper = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${opsColors.divider};
`

export const TabItem = styled.button<{ active?: boolean }>`
  padding: 0.625rem 1.25rem;
  border: none;
  background: ${(p) => (p.active ? opsColors.brand500 : 'transparent')};
  color: ${(p) => (p.active ? opsColors.textOnBrand : opsColors.textFaded)};
  font-size: 14px;
  font-weight: ${(p) => (p.active ? 700 : 400)};
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  transition: all 0.2s ease;

  &:hover {
    color: ${(p) => (p.active ? opsColors.textOnBrand : opsColors.textPrimary)};
    background: ${(p) => (p.active ? opsColors.brand500 : opsColors.surfaceHover)};
  }
`

// ============================================================
// Table
// ============================================================

export const OpsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`

export const OpsTableHead = styled.thead`
  th {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    color: ${opsColors.textFaded};
    letter-spacing: 0.5px;
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid ${opsColors.divider};
  }
`

export const OpsTableRow = styled.tr<{ blocked?: boolean; clickable?: boolean }>`
  ${(p) =>
    p.blocked &&
    css`
      background: ${opsColors.dangerBase}10;
    `}
  ${(p) =>
    p.clickable &&
    css`
      cursor: pointer;
      &:hover {
        background: ${opsColors.surfaceHover};
      }
    `}

  td {
    padding: 0.625rem 0.75rem;
    font-size: 14px;
    color: ${opsColors.textPrimary};
    border-bottom: 1px solid ${opsColors.dividerFaded};
  }
`

export const MonoText = styled.span`
  font-family: 'DM Mono', 'Courier New', monospace;
  font-size: 13px;
  color: ${opsColors.textSecondary};
`

// ============================================================
// Department Board
// ============================================================

export const DeptSelector = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`

export const DeptButton = styled.button<{ active?: boolean; accentColor?: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.active ? p.accentColor || opsColors.brand500 : opsColors.divider)};
  background: ${(p) => (p.active ? `${p.accentColor || opsColors.brand500}15` : 'transparent')};
  color: ${(p) => (p.active ? p.accentColor || opsColors.brand500 : opsColors.textSecondary)};
  font-size: 14px;
  font-weight: ${(p) => (p.active ? 700 : 400)};
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(p) => p.accentColor || opsColors.brand500};
  }
`

export const FilterPills = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
`

export const FilterPill = styled.button<{ active?: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.active ? opsColors.brand500 : opsColors.divider)};
  background: ${(p) => (p.active ? `${opsColors.brand500}20` : 'transparent')};
  color: ${(p) => (p.active ? opsColors.brand500 : opsColors.textFaded)};
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${opsColors.brand500};
  }
`

// ============================================================
// Task Card (Department Board)
// ============================================================

export const TaskCard = styled.div<{ borderColor?: string }>`
  background: ${opsColors.surface};
  border: 1px solid ${opsColors.divider};
  border-left: 3px solid ${(p) => p.borderColor || opsColors.divider};
  border-radius: 0 8px 8px 0;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const TaskCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

export const TaskCardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${opsColors.textPrimary};
`

export const TaskCardSubtitle = styled.div`
  font-size: 12px;
  color: ${opsColors.textFaded};
`

export const TaskCardBadges = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  align-items: center;
`

export const TaskCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 12px;
  color: ${opsColors.textFaded};
`

export const WarningText = styled.span`
  color: ${opsColors.warningBase};
  font-size: 12px;
  font-weight: 500;
`

export const DangerText = styled.span`
  color: ${opsColors.dangerBase};
  font-size: 12px;
  font-weight: 500;
`

// ============================================================
// Team Panel
// ============================================================

export const TeamMemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${opsColors.dividerFaded};

  &:last-child {
    border-bottom: none;
  }
`

export const TeamMemberInfo = styled.div`
  flex: 1;
  min-width: 0;

  .member-name {
    font-size: 14px;
    font-weight: 500;
    color: ${opsColors.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .member-task {
    font-size: 11px;
    color: ${opsColors.textFaded};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export const UtilizationBar = styled.div<{ percent: number }>`
  width: 50px;
  height: 4px;
  background: ${opsColors.divider};
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;

  &::after {
    content: '';
    display: block;
    width: ${(p) => p.percent}%;
    height: 100%;
    background: ${(p) =>
      p.percent > 80
        ? opsColors.successBase
        : p.percent > 40
        ? opsColors.brand500
        : opsColors.textFaded};
    border-radius: 2px;
  }
`

// ============================================================
// Horizon Board
// ============================================================

export const HorizonHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

export const HorizonLegend = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

export const LegendItem = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 11px;
  color: ${opsColors.textFaded};

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${(p) => p.color};
  }
`

export const HorizonGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const HorizonRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  align-items: center;
  min-height: 36px;
`

export const HorizonResourceName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${opsColors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const HorizonTimeline = styled.div`
  display: flex;
  gap: 2px;
  height: 32px;
  position: relative;
`

export const HorizonBlock = styled.div<{
  color: string
  textColor: string
  flex: number
  blocked?: boolean
}>`
  flex: ${(p) => p.flex};
  background: ${(p) => p.color};
  color: ${(p) => p.textColor};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 0 4px;
  transition: transform 0.15s ease;
  ${(p) =>
    p.blocked &&
    css`
      border: 1px dashed ${opsColors.dangerBase};
    `}

  &:hover {
    transform: scaleY(1.12);
  }
`

// ============================================================
// Stepper
// ============================================================

export const StepperWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  margin-bottom: 1.5rem;
`

export const StepItem = styled.div<{ active?: boolean; completed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const StepCircle = styled.div<{ active?: boolean; completed?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${(p) =>
    p.active ? opsColors.brand500 : p.completed ? opsColors.successBase : opsColors.divider};
  color: ${(p) => (p.active || p.completed ? opsColors.textOnBrand : opsColors.textFaded)};
`

export const StepLabel = styled.span<{ active?: boolean }>`
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 700 : 400)};
  color: ${(p) => (p.active ? opsColors.textPrimary : opsColors.textFaded)};
  white-space: nowrap;
`

export const StepConnector = styled.div`
  flex: 1;
  height: 1px;
  background: ${opsColors.divider};
  margin: 0 0.5rem;
  min-width: 20px;
`

// ============================================================
// Buttons
// ============================================================

export const OpsButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'danger' | 'success'
  size?: 'sm' | 'md'
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  border: none;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${(p) => {
    const size = p.size || 'md'
    return size === 'sm'
      ? css`
          padding: 0.375rem 0.75rem;
          font-size: 12px;
        `
      : css`
          padding: 0.5rem 1.25rem;
          font-size: 14px;
        `
  }}

  ${(p) => {
    switch (p.variant) {
      case 'danger':
        return css`
          background: ${opsColors.dangerBase};
          color: #fff;
          &:hover {
            background: #dc2626;
          }
        `
      case 'success':
        return css`
          background: ${opsColors.successBase};
          color: ${opsColors.textOnBrand};
          &:hover {
            background: #16a34a;
          }
        `
      case 'secondary':
        return css`
          background: ${opsColors.divider};
          color: ${opsColors.textSecondary};
          &:hover {
            background: ${opsColors.surfaceHover};
          }
        `
      default:
        return css`
          background: ${opsColors.brand500};
          color: ${opsColors.textOnBrand};
          &:hover {
            background: ${opsColors.brand700};
          }
        `
    }
  }}
`

// ============================================================
// Resource Status Panel
// ============================================================

export const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

export const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0;

  .resource-label {
    font-size: 12px;
    color: ${opsColors.textFaded};
  }

  .resource-count {
    font-size: 14px;
    font-weight: 700;
  }
`

// ============================================================
// Shift Program Card
// ============================================================

export const ShiftProgramCard = styled.div`
  background: ${opsColors.surface};
  border: 1px solid ${opsColors.divider};
  border-radius: 10px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export const ShiftProgramName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${opsColors.brand500};
`

export const ShiftProgramMeta = styled.div`
  font-size: 13px;
  color: ${opsColors.textFaded};
`

export const ShiftProgramStats = styled.div`
  display: flex;
  gap: 1.5rem;
`

// ============================================================
// Form helpers
// ============================================================

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${opsColors.textFaded};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

export const FormInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: ${opsColors.bgInput};
  border: 1px solid ${opsColors.divider};
  border-radius: 8px;
  color: ${opsColors.textPrimary};
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${opsColors.brand500};
  }

  &::placeholder {
    color: ${opsColors.textFaded};
  }
`

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: ${opsColors.bgInput};
  border: 1px solid ${opsColors.divider};
  border-radius: 8px;
  color: ${opsColors.textPrimary};
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  resize: vertical;
  min-height: 80px;

  &:focus {
    border-color: ${opsColors.brand500};
  }

  &::placeholder {
    color: ${opsColors.textFaded};
  }
`

export const FormSelect = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: ${opsColors.bgInput};
  border: 1px solid ${opsColors.divider};
  border-radius: 8px;
  color: ${opsColors.textPrimary};
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  outline: none;

  &:focus {
    border-color: ${opsColors.brand500};
  }
`

export const RadioGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`

export const RadioCard = styled.button<{ active?: boolean }>`
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.active ? opsColors.brand500 : opsColors.divider)};
  background: ${(p) => (p.active ? `${opsColors.brand500}15` : 'transparent')};
  color: ${(p) => (p.active ? opsColors.brand500 : opsColors.textSecondary)};
  font-size: 14px;
  font-weight: ${(p) => (p.active ? 700 : 400)};
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${opsColors.brand500};
  }
`

export const ReviewSection = styled.div`
  background: ${opsColors.surface};
  border: 1px solid ${opsColors.divider};
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const ReviewLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${opsColors.textFaded};
  letter-spacing: 0.5px;
`

export const ReviewValue = styled.div`
  font-size: 14px;
  color: ${opsColors.textPrimary};
  font-weight: 500;
`

export const InfoBox = styled.div<{ variant?: 'warning' | 'info' }>`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 13px;
  background: ${(p) =>
    p.variant === 'warning' ? `${opsColors.warningBase}15` : `${opsColors.infoBase}15`};
  color: ${(p) => (p.variant === 'warning' ? opsColors.warningBase : opsColors.infoBase)};
  border: 1px solid
    ${(p) => (p.variant === 'warning' ? `${opsColors.warningBase}30` : `${opsColors.infoBase}30`)};
`

export const SuccessScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;

  h2 {
    font-size: 24px;
    color: ${opsColors.textPrimary};
    margin: 0;
  }

  p {
    font-size: 14px;
    color: ${opsColors.textFaded};
  }
`
